import * as React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
} from '@mui/material';
import { KittingRule, PRIORITY_LABELS } from '../types';

// Styled confirmation for deleting a rule, matching the app's other delete
// modals (shared/components/dialogs/DeletePOConfirmation). Shows what the rule
// actually does so the wrong one is not deleted from an id alone.
export default function DeleteRuleDialog({
  rule,
  deleting,
  onConfirm,
  onCancel,
}: {
  rule: KittingRule | null;
  deleting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const detail = (label: string, value: React.ReactNode) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
      <Typography variant="subtitle2" sx={{ opacity: 0.7 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ textAlign: 'right' }}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <Dialog
      open={rule !== null}
      onClose={() => !deleting && onCancel()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ color: 'error.main' }}>Delete kitting rule?</DialogTitle>
      <DialogContent>
        {rule && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body1">
              Orders matching this rule will stop being kitted. Past history rows
              are kept, and no order that was already kitted is affected.
            </Typography>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {detail(
                  'Rule',
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {rule.ruleId}
                  </Typography>,
                )}
                {rule.description && detail('Description', rule.description)}
                {detail('Store', rule.store)}
                {detail('Priority', PRIORITY_LABELS[rule.priority] ?? rule.priority)}
                {detail('Status', rule.enabled ? 'Enabled' : 'Disabled')}
                {detail(
                  'Components',
                  <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {rule.components.map(c => `${c.quantity} × ${c.sku}`).join('  +  ')}
                  </Box>,
                )}
                {detail(
                  'Kit',
                  <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {rule.kitSku || rule.kitVariantId}
                  </Box>,
                )}
              </Box>
            </Paper>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={deleting}>
          Cancel
        </Button>
        <Button variant="contained" color="error" onClick={onConfirm} disabled={deleting}>
          {deleting ? 'Deleting…' : 'Delete rule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
